import Alpine from 'alpinejs';
import {
    handleAuthenticationRequiredResponse,
    readJsonResponse
} from '../../utils/authResponse.js';
import { getCsrfToken } from '../../utils/helpers.js';

Alpine.store('share', {
    type: null, // 'wishlist' or 'property'
    shareUrl: '',
    copied: false,
    title: '',
    emailMode: false,
    recipientEmail: '',
    isSending: false,
    emailSuccess: false,
    emailError: '',
    // Identifies the one wishlist whose unsent email draft may be restored.
    restoreEmailForWishlistUid: '',

    // Wishlist-specific
    wishlistUid: '',
    mosaicHtml: '',
    quantity: 0,
    listingIds: [],

    // Property-specific
    image: '',
    subtitle: '',

    openWishlist(uid) {
        const wishlistUid = uid || '';
        // Restore only the draft that encountered session expiry. Opening a
        // different wishlist starts with the normal clean share state.
        const shouldRestoreEmailDraft =
            this.restoreEmailForWishlistUid === wishlistUid;

        this.restoreEmailForWishlistUid = '';
        this.type = 'wishlist';
        this.copied = false;
        this.isSending = false;
        this.emailSuccess = false;
        this.emailError = '';
        this.wishlistUid = wishlistUid;

        if (!shouldRestoreEmailDraft) {
            this.emailMode = false;
            this.recipientEmail = '';
        }

        const baseUrl = window.location.origin;
        this.shareUrl = `${baseUrl}/wishlist/${wishlistUid}`;

        // Get the mosaic HTML and metadata from the hidden element
        const mosaicEl = document.querySelector(
            `[data-wishlist-mosaic="${wishlistUid}"]`
        );
        this.mosaicHtml = mosaicEl ? mosaicEl.innerHTML : '';
        this.title = mosaicEl?.getAttribute('data-wishlist-title') || '';
        this.quantity = parseInt(
            mosaicEl?.getAttribute('data-wishlist-quantity') || '0',
            10
        );

        const rawIds = mosaicEl?.getAttribute('data-wishlist-ids') || '';
        this.listingIds = rawIds
            ? rawIds.split(',').map((id) => parseInt(id, 10))
            : [];

        Alpine.store('modals')?.open('share-modal');
    },

    openProperty({ title, image, url, subtitle }) {
        // Switching share targets intentionally discards wishlist-only draft
        // recovery so it cannot surface during a later unrelated action.
        this.restoreEmailForWishlistUid = '';
        this.type = 'property';
        this.copied = false;
        this.emailMode = false;
        this.recipientEmail = '';
        this.isSending = false;
        this.emailSuccess = false;
        this.emailError = '';
        this.wishlistUid = '';
        this.title = title || '';
        this.image = image || '';
        this.subtitle = subtitle || '';
        const raw = url || window.location.href;
        this.shareUrl = raw.split('?')[0];

        Alpine.store('modals')?.open('share-modal');
    },

    close() {
        // A deliberate close abandons the recovery draft just like any other
        // unsent share draft.
        this.restoreEmailForWishlistUid = '';
        this.type = null;
        this.emailMode = false;
        this.recipientEmail = '';
        this.isSending = false;
        this.emailSuccess = false;
        this.emailError = '';
        this.wishlistUid = '';
        Alpine.store('modals')?.close('share-modal');
    },

    get emailSubject() {
        if (this.type === 'wishlist') {
            return 'Check out my Avari collection';
        }
        return `Check out ${this.title || 'this property'} on Avari`;
    },

    get emailBody() {
        if (this.type === 'wishlist') {
            return `I wanted to share my collection with you: ${this.shareUrl}`;
        }
        return `I thought you might like this: ${this.shareUrl}`;
    },

    async copyLink() {
        try {
            await navigator.clipboard.writeText(this.shareUrl);
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            const input = document.getElementById('share-url-input');
            if (input) {
                input.select();
                document.execCommand('copy');
                this.copied = true;
                setTimeout(() => {
                    this.copied = false;
                }, 2000);
            }
        }
    },

    async sendWishlistEmail() {
        if (this.type !== 'wishlist') {
            return;
        }

        const rawEmail = (this.recipientEmail || '').trim();
        if (!rawEmail) {
            this.emailError = 'Please enter at least one email address.';
            return;
        }

        // Refresh before sending so an expired session reaches the structured
        // auth response instead of failing stale-CSRF validation first.
        const csrf = await getCsrfToken(true);
        if (!csrf || !csrf.name || !csrf.value) {
            this.emailError =
                "We couldn't verify your request. Please refresh the page and try again.";
            return;
        }

        this.isSending = true;
        this.emailError = '';

        try {
            const formData = new FormData();
            formData.append('recipientEmail', rawEmail);
            formData.append('wishlistName', this.title || 'Shared Wishlist');
            formData.append('wishlistId', this.wishlistUid || '');
            this.listingIds.forEach((id) =>
                formData.append('listingIds[]', String(id))
            );
            formData.append(csrf.name, csrf.value);

            const response = await fetch('/actions/avari/wishlist/share', {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            // Parse safely so the stable auth contract can be classified
            // before ordinary malformed-response handling runs.
            const data = await readJsonResponse(response);

            if (
                handleAuthenticationRequiredResponse(response, data, {
                    userStore: Alpine.store('user'),
                    modalsStore: Alpine.store('modals')
                })
            ) {
                // Preserve the draft for one explicit reopen, but never resend
                // the email automatically after login.
                this.restoreEmailForWishlistUid = this.wishlistUid;
                return;
            }

            if (!data) {
                throw new Error('Wishlist API returned an invalid response');
            }

            if (!response.ok || !data?.success) {
                this.emailError =
                    data?.error ||
                    "We couldn't share this collection. Please try again.";
                return;
            }

            this.emailSuccess = true;
            this.emailMode = false;
            this.recipientEmail = '';
        } catch (err) {
            this.emailError =
                "We couldn't share this collection. Please try again.";
            console.error(err);
        } finally {
            this.isSending = false;
        }
    }
});
